<script setup lang="ts">
import type { StudyModel } from '@/models/study';
import { nextTick, onMounted, ref, type PropType } from 'vue';
import { StudyAvatar } from '@/components/common';

const props = defineProps({
  studies: {
    type: Array as PropType<StudyModel[]>,
    required: true
  },
  selectedStudyId: {
    type: String
  }
});

const emit = defineEmits(['studyClick', 'close']);

const listRef = ref<HTMLElement | null>(null);

const setSelected = (item: StudyModel) => {
  emit('studyClick', item);
};

const optionElements = () =>
  Array.from(listRef.value?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);

// Wraps, so Down from the last option lands back on the first.
const focusOption = (index: number) => {
  const options = optionElements();
  if (!options.length) {
    return;
  }
  options[(index + options.length) % options.length].focus();
};

const onOptionKeydown = (event: KeyboardEvent, item: StudyModel, index: number) => {
  switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault();
      setSelected(item);
      break;
    case 'ArrowDown':
      event.preventDefault();
      focusOption(index + 1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      focusOption(index - 1);
      break;
    case 'Home':
      event.preventDefault();
      focusOption(0);
      break;
    case 'End':
      event.preventDefault();
      focusOption(-1);
      break;
    case 'Escape':
      event.preventDefault();
      emit('close');
      break;
  }
};

// The popup is teleported to the end of <body>, so Tab from the trigger would
// skip straight past it. Move focus in on open, starting on the current study.
onMounted(async () => {
  await nextTick();
  const current = props.studies?.findIndex(study => study.identifier === props.selectedStudyId) ?? -1;
  focusOption(current < 0 ? 0 : current);
});
</script>

<template>
    <div class="study-selector-popup" ref="target">
      <div class="popup-heading">Studies</div>
      <ul role="listbox" ref="listRef" aria-label="Studies">
        <li v-for="(item, index) in studies"
          @click="setSelected(item)"
          @keydown="onOptionKeydown($event, item, index)"
          :key="item.identifier"
          role="option"
          tabindex="0"
          :class="{ current: item.identifier === selectedStudyId }"
          :aria-selected="item.identifier === selectedStudyId">
          <StudyAvatar :avatar="item.avatar" :title="item.title" :size="28" />
          <span class="study-title">{{ item.title }}</span>
          <FontAwesomeIcon v-if="item.identifier === selectedStudyId"
            :icon="['fas', 'check']"
            class="check" />
        </li>
      </ul>
    </div>
</template>

<style scoped lang="scss">
@use '../../assets/variables.module' as *;
.study-selector-popup{
  min-width: 240px;
  max-width: 320px;
  padding: 4px;
}
.popup-heading {
  // Stays put while the list scrolls under it (the Popup owns the scrolling).
  position: sticky;
  top: -4px;
  z-index: 1;
  padding: 8px 8px 4px;
  background-color: white;
  color: hsla(0, 0%, 0%, 0.54);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
  font-size: 15px;
}
li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.12s ease;
  &:hover {
    background: hsl(210, 20%, 96%);
  }
  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: -2px;
  }
}
.study-title {
  // Long study names wrap to a second line and then ellipsis.
  flex: 1;
  min-width: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.check {
  flex: 0 0 auto;
  font-size: 12px;
  color: $primary-color;
}
.current {
  background: hsl(248.5, 100%, 96%);
  font-weight: 500;
  &:hover {
    background: hsl(248.5, 100%, 93%);
  }
}
</style>
